import { Link } from "react-router-dom";
import { Moon, Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { api, formatApiErr } from "@/lib/api";
import { WHATSAPP_LINK, INSTAGRAM, FACEBOOK, YOUTUBE, openWhatsApp } from "@/data/site";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useLang();

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Subscribed! Watch your inbox for cosmic updates.");
            setEmail("");
        } catch (err) {
            toast.error(formatApiErr(err.response?.data?.detail) || "Subscription failed");
        } finally { setLoading(false); }
    };

    return (
        <footer className="relative bg-ink-deep border-t border-gold/10 mt-20" data-testid="footer">
            <div className="container-vv py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    <div>
                        <Link to="/" className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full overflow-hidden border border-gold/40 bg-cream flex-shrink-0">
                                <img src="https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/ad62uzli_IMG_3335.png" alt="VedicByTannu logo" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <div className="font-serif text-lg">VedicByTannu</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-gold/80">{t("hero.tagline")}</div>
                            </div>
                        </Link>
                        <p className="mt-5 text-sm text-cream-muted leading-relaxed">
                            {t("footer.tagline")}
                        </p>
                    </div>

                    <div>
                        <div className="label-eyebrow mb-4">{t("footer.explore")}</div>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/about" className="text-cream hover:text-gold">{t("footer.about_link")}</Link></li>
                            <li><Link to="/services" className="text-cream hover:text-gold">{t("footer.services_link")}</Link></li>
                            <li><Link to="/courses" className="text-cream hover:text-gold">{t("footer.courses_link")}</Link></li>
                            <li><Link to="/book" className="text-cream hover:text-gold">{t("footer.book_link")}</Link></li>
                            <li><Link to="/booking" className="text-cream hover:text-gold">{t("footer.booking_link")}</Link></li>
                            <li><Link to="/blog" className="text-cream hover:text-gold">{t("footer.blog_link")}</Link></li>
                            <li><Link to="/faq" className="text-cream hover:text-gold">{t("footer.faq_link")}</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div className="label-eyebrow mb-4">{t("footer.reach")}</div>
                        <ul className="space-y-3 text-sm text-cream-muted">
                            <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-1 text-gold" /> info@vedicbytannu.com</li>
                            <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-1 text-gold" /> +91 98711 78773</li>
                            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-1 text-gold" /> {t("footer.online_worldwide")}</li>
                        </ul>
                        <div className="flex gap-3 mt-5">
                            <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full border border-gold/30 text-gold hover:bg-gold/10" data-testid="social-instagram"><Instagram className="h-4 w-4" /></a>
                            <a href={FACEBOOK}  target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full border border-gold/30 text-gold hover:bg-gold/10" data-testid="social-facebook"><Facebook className="h-4 w-4" /></a>
                            <a href={YOUTUBE}   target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full border border-gold/30 text-gold hover:bg-gold/10" data-testid="social-youtube"><Youtube className="h-4 w-4" /></a>
                            <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full border border-gold/30 text-gold hover:bg-gold/10 text-xs" data-testid="social-whatsapp">WA</a>
                        </div>
                    </div>

                    <div>
                        <div className="label-eyebrow mb-4">{t("footer.newsletter")}</div>
                        <p className="text-sm text-cream-muted mb-4">{t("footer.newsletter_blurb")}</p>
                        <form onSubmit={subscribe} className="space-y-3" data-testid="newsletter-form">
                            <input
                                type="email"
                                required
                                placeholder={t("footer.email_placeholder")}
                                className="input-luxe text-sm"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="newsletter-email"
                            />
                            <button type="submit" disabled={loading} className="btn-gold w-full text-sm disabled:opacity-50" data-testid="newsletter-submit">
                                {loading ? t("footer.subscribing") : t("footer.subscribe")}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-gold/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-cream-dim">
                    <div>© {new Date().getFullYear()} VedicByTannu. {t("footer.copyright")}</div>
                    <div className="flex gap-6">
                        <Link to="/admin/login" className="hover:text-gold" data-testid="admin-link">{t("footer.admin")}</Link>
                        <a href="#" className="hover:text-gold">{t("footer.privacy")}</a>
                        <a href="#" className="hover:text-gold">{t("footer.terms")}</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
